import React, {
	createContext,
	FC,
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {AccessRestrictedPage} from "@/app2/shared/components/AccessRestrictedPage";
import {useApp2Auth} from "@/app2/auth";
import usePropelAuth from "@/app2/shared/hooks/usePropelAuth";
import {subscribeTrainingAccessForbidden} from "@/app2/shared/utils/trainingAccessRestricted";

export {TRAINING_ACCESS_FORBIDDEN_MESSAGE, isTrainingAccessForbidden} from "@/app2/shared/utils/trainingAccessRestricted";

type AccessRestrictedContextValue = {
	isAccessRestricted: boolean;
};

export const AccessRestrictedContext = createContext<AccessRestrictedContextValue>({
	isAccessRestricted: false
});

interface IProps {
	children: ReactElement;
}

/**
 * Blocks the authenticated App2 shell when the user lacks training access.
 *
 * - Proactive: JWT roles via {@link usePropelAuth} (avoids mounting bootstrap + API burst).
 * - Reactive: single global axios interceptor for improve-api 403 training denials.
 */
export const AccessRestrictedProvider: FC<IProps> = ({children}) => {
	const [isAccessRestricted, setIsAccessRestricted] = useState(false);
	const {logout} = useApp2Auth();
	const {isTrainingMember, authInfo} = usePropelAuth();

	const markAccessRestricted = useCallback(() => {
		setIsAccessRestricted((prev) => (prev ? prev : true));
	}, []);

	useEffect(() => {
		if (authInfo.isLoggedIn && !isTrainingMember) {
			markAccessRestricted();
		}
	}, [authInfo.isLoggedIn, isTrainingMember, markAccessRestricted]);

	useEffect(() => subscribeTrainingAccessForbidden(markAccessRestricted), [markAccessRestricted]);

	const contextValue = useMemo(
		(): AccessRestrictedContextValue => ({isAccessRestricted}),
		[isAccessRestricted]
	);

	if (isAccessRestricted) {
		return (
			<AccessRestrictedContext.Provider value={contextValue}>
				<AccessRestrictedPage onLogout={logout} />
			</AccessRestrictedContext.Provider>
		);
	}

	return (
		<AccessRestrictedContext.Provider value={contextValue}>
			{children}
		</AccessRestrictedContext.Provider>
	);
};
